import React, { useState, useMemo, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Export from "../shareSalesContribution/components/Export";
import printChart from "../../../../PrintChart";
import InitialPrompt from "../../../../components/InitialPrompt";
import DefaultState from "../../components/DefaultState";
import PageHeader from "../../../../components/PageHeader";
import ExcelJS from "exceljs";
// Constants
const CHART_ID = "target-hit-chart";
const BRAND_NAME = "Angels Pizza";
const BRAND_CODE = "ANGELSPIZZA_";
const COLORS = {
  primary: "#83c14a",
  secondary: "#0076ba",
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Helper functions
const getCurrentDate = () => {
  const date = new Date();
  return {
    month: date.getMonth(),
    year: date.getFullYear(),
    monthName: MONTH_NAMES[date.getMonth()],
  };
};

const calculateGrowthRate = (currentValue, previousValue) => {
  if (previousValue <= 0) return 0;
  return ((currentValue - previousValue) / previousValue) * 100;
};

const calculateExpectedSales = (previousSales, growthRate) => {
  return previousSales * (1 + growthRate / 2 / 100) || 0;
};

const createBarChartOptions = (currentMonth, actualSales, expectedSales) => ({
  chart: { type: "column" },
  title: { text: "% Target Hit - Current Month" },
  xAxis: {
    categories: [currentMonth],
    title: { text: null },
  },
  yAxis: {
    min: 0,
    title: { text: "Gross Sales" },
  },
  series: [
    {
      name: "Actual Gross Sales",
      data: [actualSales],
      color: COLORS.primary,
    },
    {
      name: "Expected Gross Sales",
      data: [expectedSales],
      color: COLORS.secondary,
    },
  ],
  credits: { enabled: false },
});

// const downloadCSV = (data) => {
//   const { brand, store, actualSales, expectedSales } = data;
//   const csvRows = [
//     ["Brand", "Store", "Actual Sales", "Expected Sales"],
//     [brand, store, actualSales.toString(), expectedSales.toString()],
//   ];
//   const csvString = csvRows.map((e) => e.join(",")).join("\n");

//   const blob = new Blob([csvString], { type: "text/csv" });
//   const url = URL.createObjectURL(blob);
//   const a = document.createElement("a");
//   a.setAttribute("hidden", "");
//   a.setAttribute("href", url);
//   a.setAttribute("download", "sales-data.csv");
//   document.body.appendChild(a);
//   a.click();
//   document.body.removeChild(a);
// };

const downloadExcel = async ({ brand, store, actualSales, expectedSales }) => {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Target Hit");

  // Colors (ARGB)
  const BLUE = "FF0076BA";
  const GREEN = "FF83C14A";
  const LIGHT_GRAY = "FFF2F2F2";
  const BORDER = "FFCCCCCC";

  // Title
  ws.mergeCells("A1:D1");
  ws.getCell("A1").value = "Target Hit Report";
  ws.getCell("A1").font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
  ws.getCell("A1").alignment = { vertical: "middle", horizontal: "center" };
  ws.getCell("A1").fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: BLUE },
  };
  ws.getRow(1).height = 24;

  // Meta
  ws.getCell("A2").value = "Brand";
  ws.getCell("B2").value = brand;
  ws.getCell("A3").value = "Store";
  ws.getCell("B3").value = store;

  ["A2", "A3"].forEach((addr) => {
    ws.getCell(addr).font = { bold: true };
    ws.getCell(addr).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: LIGHT_GRAY },
    };
  });

  // Header row
  const headerRowIndex = 5;
  const header = ["Brand", "Store", "Actual Sales", "Expected Sales"];
  ws.getRow(headerRowIndex).values = header;
  ws.getRow(headerRowIndex).font = { bold: true, color: { argb: "FFFFFFFF" } };
  ws.getRow(headerRowIndex).alignment = {
    vertical: "middle",
    horizontal: "center",
  };
  ws.getRow(headerRowIndex).height = 18;

  // Border style helper
  const borderStyle = {
    top: { style: "thin", color: { argb: BORDER } },
    left: { style: "thin", color: { argb: BORDER } },
    bottom: { style: "thin", color: { argb: BORDER } },
    right: { style: "thin", color: { argb: BORDER } },
  };

  ws.getRow(headerRowIndex).eachCell((cell) => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: GREEN } };
    cell.border = borderStyle;
  });

  // Data row
  const dataRowIndex = headerRowIndex + 1;
  ws.getRow(dataRowIndex).values = [
    brand,
    store,
    Number(actualSales || 0),
    Number(expectedSales || 0),
  ];

  ws.getCell(`C${dataRowIndex}`).numFmt = "#,##0.00";
  ws.getCell(`D${dataRowIndex}`).numFmt = "#,##0.00";

  ws.getRow(dataRowIndex).eachCell((cell, colNumber) => {
    cell.border = borderStyle;
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFFFFFFF" },
    };
    cell.alignment =
      colNumber === 3 || colNumber === 4
        ? { vertical: "middle", horizontal: "right" }
        : { vertical: "middle", horizontal: "left" };
  });

  // Column widths
  ws.getColumn(1).width = 18;
  ws.getColumn(2).width = 22;
  ws.getColumn(3).width = 18;
  ws.getColumn(4).width = 18;

  // Freeze + filter
  ws.views = [{ state: "frozen", ySplit: headerRowIndex }];
  ws.autoFilter = {
    from: { row: headerRowIndex, column: 1 },
    to: { row: headerRowIndex, column: 4 },
  };

  // Create xlsx buffer
  const buffer = await wb.xlsx.writeBuffer();

  // Download without file-saver
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `target-hit-${store || "All"}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const Header = ({ onExportCsv, onExportPdf }) => (
  <div className="d-flex justify-content-between align-items-center">
    <PageHeader
      title="Target Hit"
      subtitle="Compare actual sales against targets for the current period"
      icon="bi bi-bullseye"
    />
    <Export exportCsv={onExportCsv} exportPdf={onExportPdf} />
  </div>
);

const Chart = ({ options }) => (
  <div className="card">
    <h6
      className="text-center mt-4 fw-bold"
      style={{ color: COLORS.secondary }}
    >
      % Target Hit - Current Month
    </h6>
    <div className="row">
      <div className="col-12">
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
    </div>
  </div>
);

const PrintContent = ({ currentMonth, store, actualSales, expectedSales }) => (
  <div className="print-only-content">
    <h2>Report Details</h2>
    <div>
      <h3>Brand: {BRAND_NAME}</h3>
      <h3>Store: {store}</h3>
      <h3>Month: {currentMonth}</h3>
    </div>
    <div style={{ fontSize: "20px" }}>
      <table className="table text-center">
        <thead>
          <tr>
            <th>Month</th>
            <th>Actual Gross Sales</th>
            <th>Expected Gross Sales</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{currentMonth}</td>
            <td>{actualSales}</td>
            <td>{expectedSales}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
);

function TargetHit({ objects, storess, store }) {
  // State
  const [selectedMonth, setSelectedMonth] = useState("");
  const [optionss, setOptionss] = useState([]);
  const [selectedOption, setSelectedOption] = useState("");

  console.log("TARGET", objects);

  // Get current date information
  const {
    month: currentMonth,
    year: currentYear,
    monthName: currentMonthName,
  } = getCurrentDate();

  // Process store codes on mount
  useEffect(() => {
    const storeCodes = objects.reduce((acc, item) => {
      const code = item.store_code.split(BRAND_CODE)[1];
      if (code && !acc.includes(code)) acc.push(code);
      return acc;
    }, []);
    setOptionss(storeCodes.map((code) => ({ value: code, label: code })));
  }, [objects]);

  // Process and filter transactions
  const parsedObjects = useMemo(() => {
    return objects.map((obj) => {
      const parts = obj.store_code.split(BRAND_CODE);
      const codePart = parts.length > 1 ? parts[1] : "";
      const date = new Date(obj.tran_date);
      return {
        ...obj,
        parsedMonth: date.toLocaleString("default", { month: "long" }),
        parsedYear: date.getFullYear().toString(),
        storeCodePart: codePart,
      };
    });
  }, [objects]);

  const filteredTransactions = useMemo(() => {
    return parsedObjects.filter((obj) => {
      const matchesStore = selectedOption
        ? obj.storeCodePart === selectedOption
        : true;
      const isComplete = obj.status === "C";
      return matchesStore && isComplete;
    });
  }, [parsedObjects, selectedOption]);

  // Calculate sales metrics
  const actualSalesCurrentMonth = filteredTransactions
    // .filter((obj) => new Date(obj.tran_date).getFullYear() === currentYear)
    .reduce((acc, curr) => acc + parseFloat(curr.total), 0);

  const salesPreviousYear = filteredTransactions
    .filter((obj) => new Date(obj.tran_date).getFullYear() === currentYear - 1)
    .reduce((acc, curr) => acc + parseFloat(curr.total), 0);

  const monthlyGrowthRate = calculateGrowthRate(
    actualSalesCurrentMonth,
    salesPreviousYear,
  );
  const expectedSalesValue = calculateExpectedSales(
    salesPreviousYear,
    monthlyGrowthRate,
  );

  // Get chart data
  const chartMonth = filteredTransactions.length
    ? filteredTransactions[0]?.parsedMonth
    : filteredTransactions[0]?.parsedMonth;

  // MONTH_NAMES[currentMonth];

  console.log("chartMonth", chartMonth);

  const chartYear = filteredTransactions.length
    ? filteredTransactions[0].parsedYear
    : currentYear.toString();

  // Get expected sales from store data or calculate
  const matchedStore = store.find((item) => item.name === storess);

  const expectedEntry = matchedStore?.expectedRelation?.find(
    (rel) => rel.Month === chartMonth && rel.year === chartYear,
  );

  console.log("expectedEntry", expectedEntry);
  const expectedGrossSales = expectedEntry
    ? parseFloat(expectedEntry.total)
    : parseFloat(0);
  // calculateExpectedSales(salesPreviousYear, monthlyGrowthRate);
  console.log("actualSalesCurrentMonth", actualSalesCurrentMonth);
  console.log("expectedGrossSales", expectedGrossSales);
  console.log("currentMonthName", currentMonthName);
  // Create chart options
  const barChartOptions = createBarChartOptions(
    // currentMonthName,
    "qwe",
    actualSalesCurrentMonth,
    expectedGrossSales,
  );

  // Event handlers
  // const handleExportCsv = () => {
  //   downloadCSV({
  //     brand: BRAND_NAME,
  //     store: storess || "All",
  //     actualSales: actualSalesCurrentMonth,
  //     expectedSales: expectedGrossSales,
  //   });
  // };
  const handleExportExcel = async () => {
    await downloadExcel({
      brand: BRAND_NAME,
      store: storess || "All",
      actualSales: actualSalesCurrentMonth,
      expectedSales: expectedGrossSales,
    });
  };

  const handleExportPdf = () => printChart(CHART_ID);

  return (
    <>
      <Header onExportCsv={handleExportExcel} onExportPdf={handleExportPdf} />

      <div className="container mt-1 mb-3">
        <div className="row" />
      </div>

      {!storess && <InitialPrompt twoFilter={false} />}

      {objects?.length === 0 && storess && (
        <div div className="text-center mt-5">
          <DefaultState />
        </div>
      )}

      <div id={CHART_ID}>
        {objects?.length > 0 && storess && <Chart options={barChartOptions} />}

        <PrintContent
          currentMonth={currentMonthName}
          store={storess}
          actualSales={actualSalesCurrentMonth}
          expectedSales={expectedGrossSales}
        />
      </div>
    </>
  );
}

export default TargetHit;
